'use client';

import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { withDisplayName } from '../../lib/display-name';
import { SBadge } from '../SBadge';
import { SIcon } from '../SIcon';
import { STooltip } from '../STooltip';

export type SSectionHeaderCardBodyChildren = ReactNode;
export type SSectionHeaderCardChildren = ReactNode;
export type SSectionHeaderCardThickness = false | 'default' | 'accent';
export type SSectionHeaderCardTitleSize = 'xs' | 'sm';
export type SSectionHeaderCardBodyPadding = 'default' | 'wide' | 'none';

/** 섹션·패널 안쪽 여백은 토큰이 정한다 — 숫자를 직접 주지 않는다. */
const BODY_PADDING_CLASS: Record<SSectionHeaderCardBodyPadding, string> = {
  default: 'p-(--sys-space-panel-padding-default)',
  wide: 'p-(--sys-space-panel-padding-wide)',
  none: '',
};

const topBorderColorClass: Record<Exclude<SSectionHeaderCardThickness, false>, string> = {
  default: 'bg-(--cmp-sectionHeaderCard-topBorder-color-default)',
  accent: 'bg-(--cmp-sectionHeaderCard-topBorder-color-accent)',
};

const titleSizeClass: Record<SSectionHeaderCardTitleSize, string> = {
  xs: 'typo-heading-xs',
  sm: 'typo-heading-sm',
};

export interface SSectionHeaderCardHeaderProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'slot' | 'title'
> {
  /** 헤더 제목 */
  title: ReactNode;
  /** 제목 크기 */
  titleSize?: SSectionHeaderCardTitleSize;
  /** 제목 앞 점 표시 여부 */
  marker?: boolean;
  /** 제목 뒤 필수 별 표시 여부 */
  required?: boolean;
  /** 도움말 툴팁 메시지 */
  helpText?: string[];
  /** 부제 */
  subtitle?: ReactNode;
  /** 헤더 우측 슬롯 */
  slot?: ReactNode;
  /** 상단 border 색상 타입. false면 표시하지 않습니다. */
  thickness?: SSectionHeaderCardThickness;
}

export interface SSectionHeaderCardBodyProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'children'
> {
  /** 바디 슬롯. 특정 컴포넌트 타입으로 제한하지 않습니다. */
  children?: SSectionHeaderCardBodyChildren;
  /**
   * 안쪽 여백. 기본은 `default`.
   * 성격이 다른 요소가 세 종류 이상 섞인 영역에만 `wide`, 표를 가장자리까지 채울 때만 `none`.
   */
  padding?: SSectionHeaderCardBodyPadding;
}

export interface SSectionHeaderCardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** SSectionHeaderCard 슬롯. 특정 컴포넌트 타입으로 제한하지 않습니다. */
  children?: SSectionHeaderCardChildren;
}

const SSectionHeaderCardBodyImpl = forwardRef<HTMLDivElement, SSectionHeaderCardBodyProps>(
  function SSectionHeaderCardBody({ padding = 'default', className, children, ...rest }, ref) {
    return (
      <div ref={ref} className={cn(BODY_PADDING_CLASS[padding], className)} {...rest}>
        {children}
      </div>
    );
  },
);

const SSectionHeaderCardHeaderImpl = forwardRef<HTMLDivElement, SSectionHeaderCardHeaderProps>(
  function SSectionHeaderCardHeader(
    {
      title,
      titleSize = 'xs',
      marker = false,
      required = false,
      helpText,
      subtitle,
      slot,
      thickness = false,
      className,
      ...rest
    },
    ref,
  ) {
    const hasHelp = helpText != null && helpText.length > 0;

    return (
      <>
        {thickness && (
          <div
            className={cn(
              'h-(--cmp-sectionHeaderCard-topBorder-thickness) w-full shrink-0',
              topBorderColorClass[thickness],
            )}
          />
        )}
        <div
          ref={ref}
          className={cn(
            'box-border flex min-h-(--cmp-sectionHeaderCard-header-minHeight) w-full shrink-0 items-center gap-(--cmp-sectionHeaderCard-header-gap) overflow-hidden border-b border-(--cmp-sectionHeaderCard-header-border) bg-(--cmp-sectionHeaderCard-header-bg) px-(--cmp-sectionHeaderCard-header-paddingX) py-(--cmp-sectionHeaderCard-header-paddingY)',
            className,
          )}
          {...rest}
        >
          {marker && <SBadge color="darkblue" className="shrink-0" />}
          <div className="flex min-w-0 shrink-0 items-center gap-[8px]">
            <span
              className={cn(
                'min-w-0 truncate text-(--sys-color-fg-brand)',
                titleSizeClass[titleSize],
              )}
            >
              {title}
            </span>
            {required && (
              <SIcon
                name="asterisk"
                size={16}
                color="var(--sys-color-fg-accent)"
                className="shrink-0"
              />
            )}
            {hasHelp && (
              <STooltip
                trigger="hover"
                icon="helpOutline"
                iconSize={16}
                color="var(--sys-color-fg-success)"
                message={helpText}
                triggerClassName="shrink-0 rounded-[4px]"
              />
            )}
          </div>
          {subtitle && (
            <span className="min-w-0 truncate text-[12px] font-normal leading-[20px] text-(--sys-color-fg-secondary)">
              {subtitle}
            </span>
          )}
          <div className="ml-auto flex min-h-sd-6 min-w-0 flex-1 items-center justify-end">
            {slot}
          </div>
        </div>
      </>
    );
  },
);

const SSectionHeaderCardRootImpl = forwardRef<HTMLDivElement, SSectionHeaderCardProps>(
  function SSectionHeaderCard({ className, children, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn(
          'box-border flex w-full flex-col items-stretch overflow-hidden rounded-[8px] border border-(--sys-color-border-default) bg-(--sys-color-bg-frame)',
          className,
        )}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

const SSectionHeaderCardRoot = withDisplayName(SSectionHeaderCardRootImpl, 'SSectionHeaderCard');
const SSectionHeaderCardHeader = withDisplayName(
  SSectionHeaderCardHeaderImpl,
  'SSectionHeaderCard.Header',
);
const SSectionHeaderCardBody = withDisplayName(
  SSectionHeaderCardBodyImpl,
  'SSectionHeaderCard.Body',
);

// Object.assign 은 첫 인자를 변형하므로 번들러가 지우지 못한다. 여기서는 대상이
// 이 모듈에서 방금 만든 값뿐이라 안전하게 pure 로 표시할 수 있다.
export const SSectionHeaderCard = /* @__PURE__ */ Object.assign(SSectionHeaderCardRoot, {
  Header: SSectionHeaderCardHeader,
  Body: SSectionHeaderCardBody,
});
